import React, { useCallback } from 'react'
import { MessageAttachments } from '@components/MessageAttachments/MessageAttachments'
import { EmbeddedAttachmentsContext } from '@components/MessageAttachments/MessageAttachmentsContext'
import { MiniappTile } from '@components/Miniapp/MiniappTile'
import { parseMiniappEmbed } from '@components/Miniapp/parseMiniappEmbed'
import { TMentionTicker } from '@components/RichTextPlate/components/plate-ui/autocomplete/types'
import { RichTextPreview } from '@components/RichTextPlate/RichTextPreview'
import { FadeInBox } from '@components/Transitions'
import { isTradingChain } from '@components/Web3/Trading/tradingConstants'
import { TokenIcon } from '@components/Web3/Trading/ui/TokenIcon'
import {
    Attachment,
    EmbeddedMessageAttachment,
    OTWMention,
    TickerAttachment,
    UnfurledLinkAttachment,
} from '@towns-protocol/sdk'
import { Box, IconButton, Paragraph, Stack, Text } from '@ui'
import { LoadingUnfurledLinkAttachment } from 'hooks/useExtractInternalLinks'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { shortAddress } from 'ui/utils/utils'
import { useUserLookupContext } from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'
import { getShortTextFromMarkdown } from 'utils/getShortTextFromMarkdown'

type MessageAttachmentPreviewProps = {
    attachment: EmbeddedMessageAttachment
    onRemove: (attachmentId: string) => void
}

export const MessageAttachmentPreview = (props: MessageAttachmentPreviewProps) => {
    const { attachment, onRemove } = props
    const channelMessageEvent = attachment.channelMessageEvent

    const { lookupUser } = useUserLookupContext()

    const onRemoveClick = useCallback(() => {
        onRemove(attachment.id)
    }, [attachment.id, onRemove])

    if (!channelMessageEvent) {
        return
    }

    const displayName = getPrettyDisplayName(lookupUser(attachment.info.userId))

    return (
        <EmbeddedAttachmentsContext.Provider value={{ isMessageAttachementContext: true }}>
            <EditorAttachmentPreview
                type="forward"
                attachments={channelMessageEvent.attachments}
                displayName={displayName}
                body={channelMessageEvent.body}
                mentions={channelMessageEvent.mentions}
                onRemoveClick={onRemoveClick}
            />
        </EmbeddedAttachmentsContext.Provider>
    )
}

type UnfurledLinkAttachmentPreviewProps = {
    attachment: UnfurledLinkAttachment
    onRemove: (attachmentId: string) => void
}

export const UnfurledLinkAttachmentPreview = (props: UnfurledLinkAttachmentPreviewProps) => {
    return (
        <EmbeddedAttachmentsContext.Provider value={{ isMessageAttachementContext: true }}>
            <UnfurledLinkPreview attachment={props.attachment} onRemove={props.onRemove} />
        </EmbeddedAttachmentsContext.Provider>
    )
}

type TickerAttachmentPreviewProps = {
    attachment: TickerAttachment
    onRemove: (address: string, chainId: string) => void
}

export const TickerAttachmentPreview = (props: TickerAttachmentPreviewProps) => {
    return (
        <EmbeddedAttachmentsContext.Provider value={{ isMessageAttachementContext: true }}>
            <TickerPreview attachment={props.attachment} onRemove={props.onRemove} />
        </EmbeddedAttachmentsContext.Provider>
    )
}

export const EditorAttachmentPreview = (props: {
    type: 'forward' | 'reply'
    displayName: string
    body: string
    mentions?: OTWMention[]
    attachments?: Attachment[]
    onRemoveClick: () => void
}) => {
    const messageAbstract = getShortTextFromMarkdown(props.body)

    return (
        <FadeInBox
            horizontal
            grow
            paddingX="md"
            paddingY="paragraph"
            background="level3"
            rounded="sm"
            boxShadow="panel"
            preset="fadeup"
        >
            <Box grow gap="paragraph">
                <Paragraph strong color="gray1">
                    {props.type === 'forward' ? 'Forward' : 'Reply to'} {props.displayName}
                    &#39;s message:
                </Paragraph>
                {messageAbstract && (
                    <RichTextPreview content={messageAbstract} mentions={props.mentions} />
                )}
                {props.attachments?.length ? (
                    <Box>
                        <MessageAttachments attachments={props.attachments} eventId={undefined} />
                    </Box>
                ) : (
                    <></>
                )}
            </Box>
            <Box centerContent>
                <IconButton icon="close" color="default" onClick={props.onRemoveClick} />
            </Box>
        </FadeInBox>
    )
}

const UnfurledLinkPreview = (props: {
    attachment: UnfurledLinkAttachment | LoadingUnfurledLinkAttachment
    onRemove?: (attachmentId: string) => void
}) => {
    const { onRemove } = props
    const { url, image, title, description, id } = props.attachment
    const rawInfo = 'info' in props.attachment ? props.attachment.info : undefined
    const onRemoveClicked = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation()
            event.preventDefault()
            onRemove?.(id)
        },
        [onRemove, id],
    )

    const onClick = useCallback(() => {
        window.open(url, '_blank')
    }, [url])

    const isLoading = 'isLoading' in props.attachment && props.attachment.isLoading === true
    const miniappEmbed = !isLoading ? parseMiniappEmbed(rawInfo) : null

    if (miniappEmbed) {
        const miniappUrl = miniappEmbed.button.action.url || url
        return (
            <FadeInBox
                hoverable
                maxWidth="250"
                padding="xs"
                background="level3"
                rounded="sm"
                boxShadow="panel"
                preset="fadeup"
                position="relative"
                cursor="pointer"
                onClick={() => window.open(miniappUrl, '_blank')}
            >
                <MiniappTile
                    isAttachmentPreview
                    embed={miniappEmbed}
                    miniappUrl={miniappUrl}
                    isLoading={false}
                />
                <Box position="absolute" top="xs" right="xs">
                    <IconButton
                        background="lightHover"
                        rounded="full"
                        insetX="xs"
                        size="square_xs"
                        icon="close"
                        color="default"
                        tooltip="Remove"
                        tooltipOptions={{ immediate: true }}
                        onClick={onRemoveClicked}
                    />
                </Box>
            </FadeInBox>
        )
    }

    return (
        <FadeInBox
            hoverable
            width="250"
            paddingY="sm"
            paddingX="paragraph"
            background="level3"
            rounded="sm"
            boxShadow="panel"
            preset="fadeup"
            position="relative"
            cursor="pointer"
            onClick={onClick}
        >
            <Stack
                gap="xs"
                alignContent="center"
                width="100%"
                color={isLoading ? 'gray2' : 'default'}
            >
                <Stack horizontal gap="sm" alignItems="center">
                    {isLoading && (
                        <Box horizontal gap="sm" alignItems="center" overflow="hidden">
                            <ButtonSpinner height="paragraph" />
                            <Paragraph truncate size="sm">
                                {props.attachment.url}
                            </Paragraph>
                        </Box>
                    )}
                    {image?.url && !!image.height && <IconImage url={image.url} />}
                    {title && (
                        <Text truncate strong fontSize="sm" color="gray1">
                            {title}
                        </Text>
                    )}
                    <Box grow />
                    <IconButton
                        background="lightHover"
                        rounded="full"
                        insetX="xs"
                        size="square_xs"
                        icon="close"
                        color="default"
                        tooltip="Remove"
                        tooltipOptions={{ immediate: true }}
                        onClick={onRemoveClicked}
                    />
                </Stack>
                {description ? (
                    <Box hoverable color={{ hover: 'cta2', default: 'gray2' }}>
                        <Text truncate fontSize="sm" fontWeight="medium">
                            {description}
                        </Text>
                    </Box>
                ) : (
                    <></>
                )}
            </Stack>
        </FadeInBox>
    )
}

const TickerPreview = (props: {
    attachment: TickerAttachment & { coinData?: TMentionTicker }
    onRemove?: (address: string, chainId: string) => void
}) => {
    const { onRemove, attachment } = props

    const onRemoveClicked = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation()
            event.preventDefault()
            onRemove?.(attachment.address, attachment.chainId)
        },
        [onRemove, attachment],
    )

    return (
        <Box
            hoverable
            paddingY="sm"
            paddingX="paragraph"
            background="level3"
            rounded="sm"
            position="relative"
            cursor="pointer"
        >
            <Stack horizontal gap alignItems="center" minWidth="150" justifyContent="spaceBetween">
                {attachment.coinData ? (
                    <Box horizontal gap alignItems="center" position="relative">
                        <TokenIcon
                            asset={{
                                imageUrl: attachment.coinData.imageUrl ?? '',
                                chain: isTradingChain(attachment.chainId)
                                    ? attachment.chainId
                                    : undefined,
                            }}
                        />
                        <Paragraph truncate strong size="sm">
                            {attachment.coinData.symbol}
                        </Paragraph>
                    </Box>
                ) : (
                    <Box centerContent width="100" height="x4">
                        <Text truncate>{shortAddress(attachment.address)}</Text>
                    </Box>
                )}

                <IconButton
                    background="lightHover"
                    rounded="full"
                    size="square_xs"
                    icon="close"
                    color="default"
                    tooltip="Remove"
                    tooltipOptions={{ immediate: true }}
                    onClick={onRemoveClicked}
                />
            </Stack>
        </Box>
    )
}

const IconImage = ({ url }: { url: string }) => {
    return (
        <Box
            shrink={false}
            rounded="xs"
            insetLeft="xxs"
            width="x3"
            aspectRatio="1/1"
            style={{
                backgroundImage: `url(${url})`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
            }}
        />
    )
}
